import React, { useEffect, useState } from 'react';
import ProviderHero from './ProviderHero';
import ProviderBio from './ProviderBio';
import Video from 'components/atoms/Video';
import ProviderLocations from './ProviderLocations';
import ProviderSchedulingWidget from './ProviderSchedulingWidget';
// import ProviderReviews from './ProviderReviews';
import ActionBar from '../shared/ActionBar';
import Sidebar from '../shared/Sidebar';
import { Certification, ProviderFields, Training } from 'components/types/ProviderTypes';
import { ScheduleDay } from 'components/types/ScheduleDay';
import axios from 'axios';
import Constants from '../../../assets/utils/constant';
import Insurance from '../shared/Insurance';

export type ProviderProfileProps = {
	rendering: {
		fields: ProviderFields;
	};
};

const ProviderProfile = (props: ProviderProfileProps): JSX.Element => {
	const [scheduleDays, setScheduleDays] = useState(Array<ScheduleDay>());
	const [deptDays, setDeptDays] = useState({} as { [key: string]: string[] });
	const [providerSchedulable, setProviderSchedulable] = useState(false);
	const { rendering } = props;
	const fields = rendering.fields;
	const specialties = fields?.Specialty?.value
		?.split('|')
		.map((s) => s.trim())
		.filter((s) => s.length > 0);

	const getDeptDays = (deptId: string) => {
		const filtered = scheduleDays?.filter(
			(day) => day.Department && day.Department.ID === deptId && day.Slots.length > 0
		);

		const availableDates = filtered.slice(0, 3).map((day) => day.Date);
		availableDates.length > 0 && setProviderSchedulable(true);
		return availableDates;
	};

	const mapDeptDates = () => {
		const deptDates: { [key: string]: string[] } = {};
		fields?.Locations?.forEach((location) => {
			const deptApts = getDeptDays(location?.fields?.EpicDeptId?.value);
			if (deptApts.length > 0) {
				deptDates[location?.fields?.EpicDeptId?.value] = deptApts;
			}
		});
		setDeptDays(deptDates);
	};

	const getProviderAppts = async () => {
		const today = new Date();

		const formatDate = (date: Date) => date.toISOString().split('T')[0];

		const dateRanges = [
			{
				start: today,
				end: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000),
			},
			{
				start: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000 + 1),
				end: new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000),
			},
			{
				start: new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000 + 1),
				end: new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000),
			},
		];

		try {
			const apiCalls = dateRanges.map((range) =>
				axios.post(
					`${Constants.api.epicApi}${Constants.api.getScheduleDays}`,
					{
						StartDate: formatDate(range.start),
						EndDate: formatDate(range.end),
						ProviderID: fields?.EpicId?.value,
						ProviderIDType: 'External',
						VisitTypeIDs: [{ ID: '1003', Type: 'External' }],
					},
					{
						headers: {
							'Content-Type': 'application/json',
							'Epic-Client-ID': Constants.api.epicClientID,
							Accept: 'application/json',
							Authorization: `Basic ${Constants.api.epicAccessToken}`,
						},
					}
				)
			);

			const responses = await Promise.all(apiCalls);

			const allScheduleDays = responses.flatMap((response) => response.data.ScheduleDays);

			return allScheduleDays;
		} catch (error) {
			console.error('Error fetching next appointments:', error);
			throw error;
		}
	};

	const getVideoId = (videoUrl: string) => {
		let videoId = '';
		if (videoUrl.includes('youtube') || videoUrl.includes('youtu.be')) {
			if (videoUrl.includes('=')) {
				// url is in https://youtube.com/watch?v=abcdefg format
				videoId = videoUrl.substring(videoUrl.lastIndexOf('=') + 1);
			} else if (videoUrl.includes('.be/')) {
				// url is in https://youtu.be/abcdefg format
				videoId = videoUrl.substring(videoUrl.lastIndexOf('/') + 1);
			}
		}
		return videoId;
	};

	useEffect(() => {
		getProviderAppts().then((scheduleDays) => setScheduleDays(scheduleDays));
	}, []);

	useEffect(() => {
		scheduleDays && mapDeptDates();
	}, [scheduleDays]);

	return (
		<div className="profile">
			<ProviderHero
				img={fields?.ProviderImage && fields?.ProviderImage}
				name={fields?.FullName?.value && fields?.FullName?.value}
				degrees={fields?.Degrees?.value && fields?.Degrees?.value}
				specialties={specialties && specialties}
				clinicalFocus={fields && fields['Clinical Focus'] && fields['Clinical Focus']}
				rating={fields?.Rating?.value && fields?.Rating?.value}
				reviewCount={fields?.ReviewCount?.value && fields?.ReviewCount?.value}
				accepting={fields?.AcceptingNewPatients?.value && fields?.AcceptingNewPatients?.value}
				locations={fields?.Locations && fields?.Locations}
			/>
			<div className="overhauled">
				<div className="jss-inner-container">
					<div className="profile-layout">
						<div className="left-col">
							<ProviderBio
								background={fields?.PersonalInterests}
								specialties={specialties && specialties}
								gender={fields?.Gender?.value}
								spokenLanguages={fields?.SpokenLanguages?.value}
								certifications={fields?.Certifications?.sort((a: Certification, b: Certification) =>
									b?.fields?.YearCertified?.value.localeCompare(a?.fields?.YearCertified?.value)
								)}
								trainings={fields?.Training?.sort((a: Training, b: Training) =>
									b?.fields?.GraduationYear?.value?.localeCompare(a?.fields?.GraduationYear?.value)
								)}
								bio={fields?.AboutTheProvider}
							/>
							{fields?.VideoUrl?.value && (
								<Video
									videoId={getVideoId(fields?.VideoUrl?.value)}
									aditionalClass="provider-video"
									additionalId="provider-video"
								/>
							)}
							{fields?.Locations ? (
								<ProviderLocations
									locations={fields?.Locations && fields?.Locations}
									providerId={fields?.EpicId?.value && fields?.EpicId?.value}
									providerName={fields?.FullName?.value && fields?.FullName?.value}
									ksb={fields?.KSBProvider?.value && fields?.KSBProvider?.value}
									deptDays={deptDays && deptDays}
								/>
							) : (
								<></>
							)}
							<Insurance />
							{/* <ProviderReviews name="reviews" /> */}
							<ActionBar print copy suggestEdit />
						</div>
						{fields?.Locations?.length > 0 && fields?.KSBProvider?.value != true ? (
							<div className="right-col">
								<Sidebar header="Schedule Appointment" pageType="provider">
									<ProviderSchedulingWidget
										providerHeading={
											(fields?.FullName?.value && fields?.FullName?.value) +
											', ' +
											(fields?.Degrees?.value && fields?.Degrees?.value)
										}
										providerEpicId={fields?.EpicId?.value && fields?.EpicId?.value}
										locations={fields?.Locations && fields?.Locations}
										providerSchedulable={providerSchedulable && providerSchedulable}
										deptDays={deptDays && deptDays}
									/>
								</Sidebar>
							</div>
						) : (
							''
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProviderProfile;
